// ═══════════════════════════════════════════════════════════════
// PIPELINE HELP OVERLAY — How to use the cultivation pipeline
// Path: src/components/cultivation/PipelineHelpOverlay.jsx
// ═══════════════════════════════════════════════════════════════

import React from "react";
import {
  ALL_PIPELINE_STAGES,
  getPipelineStageColors,
} from "../../utils/cultivationUtils";

export const PipelineHelpOverlay = ({ palette, onClose }) => {
  const stageColorMap = getPipelineStageColors(palette);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 1100,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: Math.min(
            520,
            typeof window !== "undefined" ? window.innerWidth - 32 : 500
          ),
          maxHeight: "85vh",
          overflowY: "auto",
          background: palette.cardBg,
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          zIndex: 1101,
          padding: "24px 28px",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 800,
              color: "#1f2937",
            }}
          >
            How to Use the Cultivation Pipeline
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "#f3f4f6",
              border: "none",
              fontSize: 16,
              cursor: "pointer",
              padding: "4px 10px",
              borderRadius: 6,
              color: "#6b7280",
              fontWeight: 700,
            }}
          >
            ✕
          </button>
        </div>

        {/* The Five Stages */}
        <div style={{ marginBottom: 20 }}>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#374151",
              margin: "0 0 8px",
            }}
          >
            The Five Stages
          </h3>
          <p
            style={{
              fontSize: 13,
              color: "#4b5563",
              lineHeight: 1.5,
              margin: "0 0 10px",
            }}
          >
            Prospects start on the far left. As they join and engage, they move
            right through the member stages.
          </p>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}
          >
            {ALL_PIPELINE_STAGES.map((s) => {
              const sc = stageColorMap[s.key];
              return (
                <div
                  key={s.key}
                  style={{
                    background: sc.columnBg,
                    border: `1px solid ${sc.borderColor}`,
                    borderRadius: 6,
                    padding: "8px 10px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 13,
                      color: sc.color,
                      marginBottom: 2,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.3 }}
                  >
                    {s.instruction}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Reading a Card */}
        <div style={{ marginBottom: 20 }}>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#374151",
              margin: "0 0 8px",
            }}
          >
            Reading a Card
          </h3>
          <div style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.7 }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#dc2626",
                  lineHeight: 1,
                  flexShrink: 0,
                  width: 36,
                  textAlign: "center",
                }}
              >
                42
              </span>
              <div>
                <strong>Large number</strong> = days since last contact. This
                tells you when you last reached out to this person.
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#6d28d9",
                  background: "#6d28d918",
                  padding: "2px 8px",
                  borderRadius: 8,
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                65 pts
              </span>
              <div>
                <strong>Score</strong> = engagement level (0–150). Higher means
                stronger relationship.
              </div>
            </div>
          </div>
        </div>

        {/* Color System */}
        <div style={{ marginBottom: 20 }}>
          <h3
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#374151",
              margin: "0 0 8px",
            }}
          >
            The Color System
          </h3>
          <p
            style={{
              fontSize: 13,
              color: "#4b5563",
              lineHeight: 1.5,
              margin: "0 0 10px",
            }}
          >
            The colored bar on the left edge of each card tells you how urgently
            someone needs your attention:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              {
                color: "#ea580c",
                label: "Orange — Prospect",
                desc: "A non-member being cultivated. Focus on building the relationship.",
              },
              {
                color: "#dc2626",
                label: "Red — Overdue",
                desc: "No contact in 30+ days, or never contacted. Reach out today.",
              },
              {
                color: "#d97706",
                label: "Amber — Getting Stale",
                desc: "15–30 days since contact. Schedule a check-in this week.",
              },
              {
                color: "#65a30d",
                label: "Yellow-Green — Okay",
                desc: "Contact within last month. Relationship is warm.",
              },
              {
                color: "#16a34a",
                label: "Green — Recent",
                desc: "Contacted in the last two weeks. On track.",
              },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 8px",
                  background: "#f9fafb",
                  borderRadius: 6,
                  borderLeft: `6px solid ${item.color}`,
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.3 }}
                  >
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How to work */}
        <div
          style={{
            background: "#f0f9ff",
            border: "1px solid #bae6fd",
            borderRadius: 8,
            padding: "12px 14px",
          }}
        >
          <h3
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#0c4a6e",
              margin: "0 0 6px",
            }}
          >
            💡 How to Work the Pipeline
          </h3>
          <ol
            style={{
              margin: 0,
              paddingLeft: 18,
              fontSize: 13,
              color: "#1e3a5f",
              lineHeight: 1.7,
            }}
          >
            <li>
              <strong>Start with Prospects.</strong> These are people you met at
              events who haven't joined yet.
            </li>
            <li>
              <strong>Look for red bars.</strong> These people haven't heard
              from you in too long.
            </li>
            <li>
              <strong>Read the action box.</strong> It tells you exactly what to
              do next.
            </li>
            <li>
              <strong>Click a card</strong> to see their full contact history
              and plan your approach.
            </li>
            <li>
              <strong>Log your contact</strong> after you reach out — scores
              update automatically.
            </li>
            <li>
              <strong>Convert prospects</strong> by clicking their card and
              using "Convert to member."
            </li>
          </ol>
        </div>

        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 24px",
              background: "#1f2937",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </>
  );
};
